import * as React from "react";
import { Frown, Meh, Smile, type LucideIcon } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { type ReviewTone } from "./utils";

export type ReviewSentimentFilter = "positive" | "neutral" | "negative";

export const REVIEW_SENTIMENT_CONFIG: Record<
  ReviewSentimentFilter,
  {
    title: string;
    Icon: LucideIcon;
    tone: ReviewTone;
    activeClass: string;
    idleClass: string;
    rowActiveClass: string;
    rowIdleClass: string;
  }
> = {
  positive: {
    title: "Positive",
    Icon: Smile,
    tone: "positive",
    activeClass: "bg-emerald-500 hover:bg-emerald-600 border-emerald-500 text-white",
    idleClass: "text-slate-500 hover:text-emerald-500 hover:bg-emerald-50 border-slate-200",
    rowActiveClass: "bg-emerald-500 border-emerald-500 text-white",
    rowIdleClass:
      "bg-white border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50",
  },
  neutral: {
    title: "Neutral",
    Icon: Meh,
    tone: "caution",
    activeClass: "bg-yellow-500 hover:bg-yellow-600 border-yellow-500 text-white",
    idleClass: "text-slate-500 hover:text-yellow-500 hover:bg-yellow-50 border-slate-200",
    rowActiveClass: "bg-yellow-500 border-yellow-500 text-white",
    rowIdleClass:
      "bg-white border-slate-200 text-slate-500 hover:border-yellow-300 hover:text-yellow-600 hover:bg-yellow-50",
  },
  negative: {
    title: "Negative",
    Icon: Frown,
    tone: "negative",
    activeClass: "bg-rose-500 hover:bg-rose-600 border-rose-500 text-white",
    idleClass: "text-slate-500 hover:text-rose-500 hover:bg-rose-50 border-slate-200",
    rowActiveClass: "bg-rose-500 border-rose-500 text-white",
    rowIdleClass: "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600 hover:bg-rose-50",
  },
};

const SENTIMENTS = (Object.keys(REVIEW_SENTIMENT_CONFIG) as ReviewSentimentFilter[]).map(id => ({
  id,
  ...REVIEW_SENTIMENT_CONFIG[id],
}));

type ReviewSentimentFilterButtonsProps = {
  value: ReviewSentimentFilter | null;
  onChange: (value: ReviewSentimentFilter | null) => void;
  /** `icons` = circular icon buttons; `row` = full-width labeled cards (column header popover). */
  variant?: "icons" | "row";
};

export function ReviewSentimentFilterButtons({
  value,
  onChange,
  variant = "icons",
}: ReviewSentimentFilterButtonsProps) {
  if (variant === "row") {
    return (
      <div className="grid grid-cols-3 gap-1.5 w-full" role="group" aria-label="Sentiment">
        {SENTIMENTS.map(({ id, title, Icon, rowActiveClass, rowIdleClass }) => {
          const selected = value === id;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={selected}
              title={title}
              className={cn(
                "flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2.5 transition-colors cursor-pointer border",
                selected ? rowActiveClass : rowIdleClass
              )}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onChange(selected ? null : id);
              }}
            >
              <Icon className="size-5 pointer-events-none" />
              <span className="text-[10px] font-bold uppercase tracking-wide leading-none pointer-events-none">
                {title}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-row gap-2">
      {SENTIMENTS.map(({ id, title, Icon, activeClass, idleClass }) => {
        const selected = value === id;
        return (
          <Button
            key={id}
            type="button"
            variant={selected ? "default" : "outline"}
            size="icon"
            className={cn("rounded-full transition-all border", selected ? activeClass : idleClass)}
            onClick={() => onChange(selected ? null : id)}
            title={title}
          >
            <Icon className="size-5" />
          </Button>
        );
      })}
    </div>
  );
}
